import { mode } from "@chakra-ui/theme-tools"
import type {
  SystemStyleObject,
  SystemStyleFunction,
} from "@chakra-ui/theme-tools"
import { $color } from "@/utils/cssVarUtils"
import { sizesOptions } from "../common/sizeOptions"

const baseStyle: SystemStyleObject = {
  lineHeight: "1.2",
  borderRadius: "md",
  fontWeight: "semibold",
  transitionProperty: "common",
  transitionDuration: "normal",
  _focusVisible: {
    boxShadow: "outline",
  },
  _disabled: {
    opacity: 0.4,
    cursor: "not-allowed",
    boxShadow: "none",
  },
  _hover: {
    _disabled: {
      bg: "initial",
    },
  },
}

function getColors(c: string) {
  return {
    normal: $color(`scene-${c}-normal`),
    active: $color(`scene-${c}-active`),
    currColor: $color(`text-normal`),
    lightColor: $color(`light-text-normal`),
    darkColor: $color(`dark-text-normal`),
  }
}

const variantGhost: SystemStyleFunction = (props) => {
  const { colorScheme } = props
  const {
    normal,
    active,
    currColor,
    lightColor,
    darkColor,
  } = getColors(colorScheme)

  let hoverColor = darkColor
  if (['success', 'warning'].includes(colorScheme)) {
    hoverColor = lightColor
  }

  return {
    color: currColor,
    bg: "transparent",
    _hover: {
      bg: active,
      color: hoverColor,
    },
    _active: {
      bg: '',
    },
  }
}

const variantOutline: SystemStyleFunction = (props) => {
  const { colorScheme } = props
  const {
    normal,
    active,
  } = getColors(colorScheme)

  return {
    border: "none",
    boxShadow: `inset ${normal} 0 0 0 1px`,
    color: normal,
    _hover: {
      boxShadow: `inset ${active} 0 0 0 1px`,
      color: active,
    },
    _focus: {
      boxShadow: ''
    }
  }
}


const variantSolid: SystemStyleFunction = (props) => {
  const { colorScheme } = props
  const {
    normal,
    active,
    currColor,
    lightColor,
    darkColor,
  } = getColors(colorScheme)

  let color = lightColor
  if (['primary', 'danger', 'purple'].includes(colorScheme)) {
    color = darkColor
  } else if (['secondary'].includes(colorScheme)) {
    color = currColor
  }

  return {
    bg: normal,
    color,
    _hover: {
      bg: active,
      color: colorScheme === 'secondary' ? darkColor : color,
      _disabled: {
        bg: normal,
      },
    },
    _active: {
      bg: '',
    },
  }
}

const variantLink: SystemStyleFunction = (props) => {
  const { colorScheme } = props
  const {
    normal,
    active,
  } = getColors(colorScheme)

  return {
    padding: 0,
    height: "auto",
    lineHeight: "normal",
    verticalAlign: "baseline",
    color: normal,
    _hover: {
      textDecoration: "underline",
      color: active,
      _disabled: {
        textDecoration: "none",
      },
    },
    _active: {
      color: '',
    },
  }
}


const variantUnstyled: SystemStyleObject = {
  bg: "none",
  color: "inherit",
  display: "inline",
  lineHeight: "inherit",
  m: 0,
  p: 0,
}

const variants = {
  ghost: variantGhost,
  outline: variantOutline,
  solid: variantSolid,
  link: variantLink,
  unstyled: variantUnstyled,
}

const sizes: Record<string, SystemStyleObject> = {
  ...sizesOptions,
}

const defaultProps = {
  variant: "solid",
  size: "md",
  colorScheme: "primary",
}

export const Button = {
  baseStyle,
  variants,
  sizes,
  defaultProps,
}